import { useState, useEffect, useCallback } from 'react';
import {
  collection,
  doc,
  setDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  onSnapshot,
} from 'firebase/firestore';
import { db } from '../../../firebase';
import {
  AppAction,
  KanbanTask,
  TaskStatus,
  BoardConfig,
  PriorityTier,
  TemplateType,
  Role,
  PermissionFlags,
  DataScope,
  TaskScope,
  PendingSyncEntry,
  AuditLogEntry
} from '@/types';
import { useConsumption } from './useConsumption';
import { useRecurrence } from './useRecurrence';
import {
  createTask as createTaskAction,
  updateTaskStatus,
  deleteTask as deleteTaskAction
} from '../api/taskActions';

interface UseTaskStateReturn {
  tasks: KanbanTask[];
  logs: AuditLogEntry[];
  pendingSyncCount: number;
  pendingSyncQueue: PendingSyncEntry[];
  isOnline: boolean;
  isLoading: boolean;
  error: string | null;
  syncStatus: 'idle' | 'syncing' | 'error';
  createTask: (
    title: string,
    templateType: TemplateType,
    boardId: string,
    tags?: string[],
    scope?: TaskScope,
    recurrence?: KanbanTask['recurrence'],
    dueDate?: string,
    description?: string,
    priorityTier?: PriorityTier,
    cost?: number,
    consumption?: KanbanTask['consumption']
  ) => void;
  moveTask: (taskId: string, newStatus: TaskStatus, destinationBoard?: BoardConfig) => void;
  consumeQuantity: (taskId: string, amount: number) => void;
  deleteTask: (taskId: string) => void;
  updateTask: (taskId: string, updates: Partial<KanbanTask>) => void;
  migrateTaskScope: (taskId: string, newScope: TaskScope) => void;
  reconcileOfflineChanges: (serverTasks: KanbanTask[]) => void;
  retryPendingSync: () => Promise<void>;
  clearPendingSync: () => void;
}

export const useTaskState = (
  initialTasks: KanbanTask[],
  currentUserId: string,
  dispatch: React.Dispatch<AppAction>,
  currentHouseholdId: string | null,
  activeDataScope: DataScope,
  userPermissions?: PermissionFlags
): UseTaskStateReturn => {
  const localStorageKey = `kanban_pending_sync_${currentUserId}`;

  const [tasks, setTasks] = useState<KanbanTask[]>(initialTasks);
  const [logs, setLogs] = useState<AuditLogEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [syncStatus, setSyncStatus] = useState<'idle' | 'syncing' | 'error'>('idle');
  const [isOnline, setIsOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);
  const [pendingSyncQueue, setPendingSyncQueue] = useState<PendingSyncEntry[]>(() => {
    try {
      const stored = localStorage.getItem(localStorageKey);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  const { processConsumptionOnArchive } = useConsumption(currentUserId, currentHouseholdId, dispatch, activeDataScope, userPermissions);
  const { processTaskRecurrence } = useRecurrence(currentUserId, currentHouseholdId, dispatch, activeDataScope, userPermissions);

  useEffect(() => {
    setTasks(initialTasks);
  }, [initialTasks]);

  const persistQueue = useCallback((queue: PendingSyncEntry[]) => {
    try {
      localStorage.setItem(localStorageKey, JSON.stringify(queue));
    } catch (e) {
      console.error('Failed to persist pending sync queue:', e);
    }
  }, [localStorageKey]);

  const createLog = useCallback((entry: Omit<AuditLogEntry, 'id' | 'timestamp' | 'isOfflineSync'>) => {
    const newLog: AuditLogEntry = {
      ...entry,
      id: `log-${Math.random().toString(36).substring(2, 9)}`,
      timestamp: Date.now(),
      isOfflineSync: !navigator.onLine
    };
    setLogs(prev => [newLog, ...prev]);
  }, []);

  const getTaskCollectionRef = useCallback((taskScope: TaskScope) => {
    if (taskScope === 'personal' || !currentHouseholdId) {
      return collection(db, 'users', currentUserId, 'personalTasks');
    }
    return collection(db, 'households', currentHouseholdId, 'tasks');
  }, [currentUserId, currentHouseholdId]);

  const removeUndefined = <T extends Record<string, any>>(obj: T): Partial<T> => {
    return Object.fromEntries(
      Object.entries(obj).filter(([, value]) => value !== undefined)
    ) as Partial<T>;
  };

  const flushSyncQueue = useCallback(async () => {
    if (pendingSyncQueue.length === 0 || !navigator.onLine) return;
    setSyncStatus('syncing');
    const remaining: PendingSyncEntry[] = [];

    for (const entry of pendingSyncQueue) {
      try {
        const task = tasks.find(t => t.id === entry.taskId);
        if (!task) continue;

        if ('isDeleted' in entry.updates && entry.updates.isDeleted) {
          await deleteTaskAction(entry.taskId, { ...task, ...entry.updates } as KanbanTask, currentUserId, currentHouseholdId);
        } else {
          await updateTaskStatus(
            entry.taskId,
            (entry.updates.status as TaskStatus) || task.status,
            currentUserId,
            { ...task, ...entry.updates } as KanbanTask,
            currentHouseholdId
          );
        }
      } catch {
        if (entry.retryCount < 3) {
          remaining.push({ ...entry, retryCount: entry.retryCount + 1 });
        }
      }
    }

    setPendingSyncQueue(remaining);
    persistQueue(remaining);
    setSyncStatus(remaining.length > 0 ? 'error' : 'idle');
  }, [pendingSyncQueue, tasks, currentUserId, currentHouseholdId, persistQueue]);

  const retryPendingSync = useCallback(async () => {
    await flushSyncQueue();
  }, [flushSyncQueue]);

  const clearPendingSync = useCallback(() => {
    setPendingSyncQueue([]);
    persistQueue([]);
  }, [persistQueue]);

  // Online/offline detection — dep array includes pendingSyncQueue to avoid stale closure
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      if (pendingSyncQueue.length > 0) {
        flushSyncQueue();
      }
    };
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [pendingSyncQueue, flushSyncQueue]);

  // --- 🏗️ SCOPE-AWARE CREATION LOGIC ---
  const createTask = useCallback(async (
    title: string,
    template: TemplateType,
    boardId: string,
    tags: string[] = [],
    scope: TaskScope = 'personal',
    recurrence: KanbanTask['recurrence'] = { isEnabled: false, interval: 0, frequency: null, completionTrigger: false, lastResetDate: undefined },
    dueDate: string | undefined = undefined,
    description: string | undefined = undefined,
    priorityTier: PriorityTier = 'none',
    cost: number | undefined = undefined,
    consumption: KanbanTask['consumption'] = undefined
  ) => {
    const effectiveScope: TaskScope = (!currentHouseholdId && scope !== 'personal') ? 'personal' : scope;

    if (effectiveScope === 'household' && userPermissions) {
      if ((template === TemplateType.ACTION || template === TemplateType.MAINTENANCE) && !userPermissions.canManageTasks) {
        console.error("Unauthorized: User lacks permission to create household tasks.");
        return;
      }
      if (template === TemplateType.GROCERY && !userPermissions.canManageGroceries) {
        console.error("Unauthorized: User lacks permission to create household grocery tasks.");
        return;
      }
    }

    const isHistorical = dueDate ? new Date(dueDate).getTime() < Date.now() && template !== TemplateType.MAINTENANCE : false;

    const taskCollectionRef = getTaskCollectionRef(effectiveScope);
    const newTaskId = doc(taskCollectionRef).id;

    let finalConsumption: KanbanTask['consumption'] = undefined;
    if (template === TemplateType.GROCERY) {
      const rawConsumption = {
        quantity: 1,
        remainingQuantity: 1,
        unit: 'unit',
        stockLevel: 1,
        lastAddedHistory: undefined,
        dayBetweenRecurrence: undefined,
        averageCycle: undefined,
        cost: cost || 0,
        ...(consumption || {}),
      };
      finalConsumption = removeUndefined(rawConsumption) as KanbanTask['consumption'];
    }

    let finalRecurrence: KanbanTask['recurrence'] = undefined;
    if (template === TemplateType.MAINTENANCE) {
      const rawRecurrence = {
        ...recurrence,
        isEnabled: recurrence?.isEnabled || true,
        lastResetDate: recurrence?.lastResetDate || new Date().toISOString(),
      };
      finalRecurrence = removeUndefined(rawRecurrence) as KanbanTask['recurrence'];
    } else if (recurrence) {
      finalRecurrence = removeUndefined(recurrence) as KanbanTask['recurrence'];
    }

    const newTaskBase: Omit<KanbanTask, 'createdAt' | 'localChangeTimestamp'> = {
      id: newTaskId,
      title: title.trim(),
      description: description || '',
      status: (isHistorical ? 'history' : 'todo') as TaskStatus,
      templateType: template,
      boardId: boardId,
      lastBoardId: null,
      source: 'manual',
      tags: tags,
      ownerId: currentUserId,
      assignedTo: currentUserId,
      visibilityRole: Role.MEMBER,
      isLocked: false,
      isBaseline: false,
      scope: effectiveScope,
      priorityTier: priorityTier,
      energyRequired: 3,
      focusLevel: 'shallow',
      contributionPoints: 10,
      lastModifiedBy: currentUserId,
      isDeleted: false,
      subtasks: [],
      consumables: [],
      toolsRequired: [],
      documents: [],
      estimatedTime: 15,
      actualTime: 0,
      consumption: finalConsumption,
      recurrence: finalRecurrence,
      dueDate: dueDate,
      completedAt: isHistorical ? (dueDate || new Date().toISOString()) : undefined,
    };

    const dataToFirestore = removeUndefined(newTaskBase);

    const newTaskForLocalState: KanbanTask = {
      ...newTaskBase,
      id: newTaskId,
      createdAt: new Date().toISOString(),
      localChangeTimestamp: Date.now(),
    };

    if (!navigator.onLine) {
      const entry: PendingSyncEntry = {
        taskId: newTaskId,
        scope: effectiveScope,
        updates: { ...dataToFirestore, status: (isHistorical ? 'history' : 'todo') as TaskStatus },
        localChangeTimestamp: Date.now(),
        retryCount: 0
      };
      const newQueue = [...pendingSyncQueue, entry];
      setPendingSyncQueue(newQueue);
      persistQueue(newQueue);
      dispatch({ type: 'ADD_TASK', payload: newTaskForLocalState });
      return;
    }

    // Optimistic update — show item immediately before Firestore confirms
    setTasks(prev => [...prev, newTaskForLocalState]);

    try {
      await createTaskAction(dataToFirestore as any, currentUserId, currentHouseholdId);
      dispatch({ type: 'ADD_TASK', payload: newTaskForLocalState });
    } catch (e) {
      // Rollback optimistic update on failure
      setTasks(prev => prev.filter(t => t.id !== newTaskId));
      const entry: PendingSyncEntry = {
        taskId: newTaskId,
        scope: effectiveScope,
        updates: { ...dataToFirestore, status: (isHistorical ? 'history' : 'todo') as TaskStatus },
        localChangeTimestamp: Date.now(),
        retryCount: 0
      };
      const newQueue = [...pendingSyncQueue, entry];
      setPendingSyncQueue(newQueue);
      persistQueue(newQueue);
      console.error("Error creating task:", e);
    }
  }, [currentUserId, currentHouseholdId, userPermissions, getTaskCollectionRef, dispatch, pendingSyncQueue, persistQueue]);

  // --- 🔄 SCOPE-AWARE TRANSITION ENGINE ---
  const moveTask = useCallback(async (taskId: string, newStatus: TaskStatus, destinationBoard?: BoardConfig) => {
    const taskToMove = tasks.find(t => t.id === taskId);
    if (!taskToMove) return;

    if (taskToMove.scope === 'household' && userPermissions && !userPermissions.canManageTasks) {
      console.error("Unauthorized: User lacks permission to move household tasks.");
      return;
    }

    const isMigration = destinationBoard && destinationBoard.id !== taskToMove.boardId;
    const oldStatus = taskToMove.status;
    const oldBoardId = taskToMove.boardId;
    let updatedTask: KanbanTask = { ...taskToMove };

    if (isMigration) {
      updatedTask.boardId = destinationBoard!.id;
      updatedTask.templateType = destinationBoard!.templateType;
      updatedTask.status = destinationBoard!.allowedStatuses[0] || 'todo';
      createLog({
        actorId: currentUserId, actionType: 'MIGRATE', taskTitle: taskToMove.title,
        fromBoardId: oldBoardId, toBoardId: destinationBoard!.id, scope: taskToMove.scope
      });
    } else {
      updatedTask.status = newStatus;
    }

    if (newStatus === 'history' && updatedTask.templateType === TemplateType.GROCERY) {
      updatedTask = processConsumptionOnArchive(updatedTask, updatedTask.scope);
      createLog({
        actorId: currentUserId, actionType: 'CONSUME', taskTitle: taskToMove.title,
        fromStatus: oldStatus, toStatus: 'history', scope: updatedTask.scope
      });
    } else if ((newStatus === 'finalized' || newStatus === 'done') && updatedTask.recurrence?.isEnabled && oldStatus !== newStatus) {
      if (['todo', 'in_progress'].includes(oldStatus)) {
        const spawnedRecurrenceTask = await processTaskRecurrence(updatedTask);
        if (spawnedRecurrenceTask) {
          dispatch({ type: 'ADD_TASK', payload: spawnedRecurrenceTask });
          createLog({
            actorId: currentUserId, actionType: 'RECUR', taskTitle: updatedTask.title,
            scope: updatedTask.scope, metadata: { nextDue: spawnedRecurrenceTask.dueDate }
          });
        }
      }
      updatedTask.status = newStatus;
      updatedTask.completedAt = new Date().toISOString();
    } else if (newStatus === 'history' || newStatus === 'finalized' || newStatus === 'done') {
      updatedTask.completedAt = new Date().toISOString();
      createLog({
        actorId: currentUserId, actionType: 'MOVE', taskTitle: taskToMove.title,
        fromStatus: oldStatus, toStatus: newStatus, scope: updatedTask.scope
      });
    } else {
      createLog({
        actorId: currentUserId, actionType: 'MOVE', taskTitle: taskToMove.title,
        fromStatus: oldStatus, toStatus: newStatus, scope: updatedTask.scope
      });
    }

    updatedTask.localChangeTimestamp = Date.now();
    updatedTask.lastModifiedBy = currentUserId;

    if (!navigator.onLine) {
      const entry: PendingSyncEntry = {
        taskId,
        scope: updatedTask.scope,
        updates: {
          status: updatedTask.status,
          boardId: updatedTask.boardId,
          templateType: updatedTask.templateType,
          completedAt: updatedTask.completedAt,
          consumption: updatedTask.consumption,
          recurrence: updatedTask.recurrence,
          lastModifiedBy: updatedTask.lastModifiedBy,
        },
        localChangeTimestamp: Date.now(),
        retryCount: 0
      };
      const newQueue = [...pendingSyncQueue, entry];
      setPendingSyncQueue(newQueue);
      persistQueue(newQueue);
      dispatch({ type: 'UPDATE_TASK', payload: { id: taskId, updates: updatedTask } });
      return;
    }

    try {
      await updateTaskStatus(taskId, updatedTask.status, currentUserId, updatedTask, currentHouseholdId);
      dispatch({ type: 'UPDATE_TASK', payload: { id: taskId, updates: updatedTask } });
    } catch (e) {
      const entry: PendingSyncEntry = {
        taskId,
        scope: updatedTask.scope,
        updates: {
          status: updatedTask.status,
          boardId: updatedTask.boardId,
          templateType: updatedTask.templateType,
          completedAt: updatedTask.completedAt,
          consumption: updatedTask.consumption,
          recurrence: updatedTask.recurrence,
          lastModifiedBy: updatedTask.lastModifiedBy,
        },
        localChangeTimestamp: Date.now(),
        retryCount: 0
      };
      const newQueue = [...pendingSyncQueue, entry];
      setPendingSyncQueue(newQueue);
      persistQueue(newQueue);
      console.error("Error moving task:", e);
    }
  }, [tasks, currentUserId, userPermissions, processConsumptionOnArchive, processTaskRecurrence, createLog, dispatch, pendingSyncQueue, persistQueue, currentHouseholdId]);

  const consumeQuantity = useCallback(async (taskId: string, amount: number) => {
    const taskToConsume = tasks.find(t => t.id === taskId);
    if (!taskToConsume || !taskToConsume.consumption) return;

    if (taskToConsume.scope === 'household' && userPermissions && !userPermissions.canManageGroceries) {
      console.error("Unauthorized: User lacks permission to manage household groceries.");
      return;
    }

    const newRemaining = Math.max(0, taskToConsume.consumption.remainingQuantity - amount);
    const newStockLevel = taskToConsume.consumption.quantity > 0 ? newRemaining / taskToConsume.consumption.quantity : 0;

    const updatedConsumption = {
      ...taskToConsume.consumption,
      remainingQuantity: newRemaining,
      stockLevel: newStockLevel
    };

    const taskRef = doc(getTaskCollectionRef(taskToConsume.scope), taskId);

    try {
      await updateDoc(taskRef, {
        consumption: updatedConsumption,
        localChangeTimestamp: serverTimestamp(),
        lastModifiedBy: currentUserId
      });

      createLog({
        actorId: currentUserId, actionType: 'UPDATE', taskTitle: taskToConsume.title,
        scope: taskToConsume.scope, metadata: { remaining: newRemaining, unit: taskToConsume.consumption.unit }
      });

      if (newRemaining === 0) {
        await moveTask(taskId, 'history');
      }

      dispatch({ type: 'UPDATE_TASK', payload: { id: taskId, updates: { consumption: updatedConsumption, lastModifiedBy: currentUserId } } });
    } catch (e) {
      console.error("Error consuming quantity:", e);
    }
  }, [tasks, currentUserId, userPermissions, getTaskCollectionRef, moveTask, createLog, dispatch]);

  const reconcileOfflineChanges = useCallback((serverTasks: KanbanTask[]) => {
    const reconciled = serverTasks.map((serverTask: KanbanTask) => {
      const localUpdate = pendingSyncQueue.find(p => p.taskId === serverTask.id);
      if (localUpdate) {
        const localTime = localUpdate.localChangeTimestamp || 0;
        const serverTime = serverTask.localChangeTimestamp || 0;
        if (localTime > serverTime) {
          return { ...serverTask, ...localUpdate.updates, localChangeTimestamp: localTime };
        }
      }
      return serverTask;
    });

    setTasks(reconciled);
    setPendingSyncQueue([]);
    persistQueue([]);
    dispatch({ type: 'SET_TASKS', payload: reconciled });
  }, [pendingSyncQueue, persistQueue, dispatch]);

  const deleteTask = useCallback(async (taskId: string) => {
    const taskToDelete = tasks.find(t => t.id === taskId);
    if (!taskToDelete) return;

    if (taskToDelete.scope === 'household' && userPermissions && !userPermissions.canDeleteTasks) {
      console.error("Unauthorized: User lacks permission to delete household tasks.");
      return;
    }
    if (taskToDelete.templateType === TemplateType.GROCERY && taskToDelete.scope === 'household' && userPermissions && !userPermissions.canDeleteGroceries) {
      console.error("Unauthorized: User lacks permission to delete household grocery tasks.");
      return;
    }

    if (!navigator.onLine) {
      const entry: PendingSyncEntry = {
        taskId,
        scope: taskToDelete.scope,
        updates: { isDeleted: true, lastModifiedBy: currentUserId },
        localChangeTimestamp: Date.now(),
        retryCount: 0
      };
      const newQueue = [...pendingSyncQueue, entry];
      setPendingSyncQueue(newQueue);
      persistQueue(newQueue);
      dispatch({ type: 'REMOVE_TASK', payload: taskId });
      createLog({
        actorId: currentUserId, actionType: 'DELETE', taskTitle: taskToDelete.title,
        scope: taskToDelete.scope
      });
      return;
    }

    try {
      await deleteTaskAction(taskId, taskToDelete, currentUserId, currentHouseholdId);
      createLog({
        actorId: currentUserId, actionType: 'DELETE', taskTitle: taskToDelete.title,
        scope: taskToDelete.scope
      });
      dispatch({ type: 'REMOVE_TASK', payload: taskId });
    } catch (e) {
      const entry: PendingSyncEntry = {
        taskId,
        scope: taskToDelete.scope,
        updates: { isDeleted: true, lastModifiedBy: currentUserId },
        localChangeTimestamp: Date.now(),
        retryCount: 0
      };
      const newQueue = [...pendingSyncQueue, entry];
      setPendingSyncQueue(newQueue);
      persistQueue(newQueue);
      console.error("Error deleting task:", e);
    }
  }, [tasks, currentUserId, userPermissions, createLog, dispatch, pendingSyncQueue, persistQueue, currentHouseholdId]);

  const updateTask = useCallback(async (taskId: string, updates: Partial<KanbanTask>) => {
    const taskToUpdate = tasks.find(t => t.id === taskId);
    if (!taskToUpdate) return;

    if (taskToUpdate.scope === 'household' && userPermissions) {
      if ((taskToUpdate.templateType === TemplateType.ACTION || taskToUpdate.templateType === TemplateType.MAINTENANCE) && !userPermissions.canManageTasks) {
        console.error("Unauthorized: User lacks permission to update household tasks.");
        return;
      }
      if (taskToUpdate.templateType === TemplateType.GROCERY && !userPermissions.canManageGroceries) {
        console.error("Unauthorized: User lacks permission to update household grocery tasks.");
        return;
      }
    }

    const taskRef = doc(getTaskCollectionRef(taskToUpdate.scope), taskId);
    const cleanedUpdates = Object.fromEntries(
      Object.entries(updates).filter(([, value]) => value !== undefined)
    );

    try {
      await updateDoc(taskRef, {
        ...cleanedUpdates,
        localChangeTimestamp: serverTimestamp(),
        lastModifiedBy: currentUserId,
      });

      createLog({
        actorId: currentUserId, actionType: 'UPDATE', taskTitle: taskToUpdate.title,
        scope: taskToUpdate.scope, metadata: cleanedUpdates
      });

      dispatch({ type: 'UPDATE_TASK', payload: { id: taskId, updates: { ...cleanedUpdates, lastModifiedBy: currentUserId } } });
    } catch (e) {
      console.error("Error updating task:", e);
    }
  }, [tasks, currentUserId, userPermissions, getTaskCollectionRef, createLog, dispatch]);

  // --- 🔄 SCOPE MIGRATION LOGIC ---
  const migrateTaskScope = useCallback(async (taskId: string, newScope: TaskScope) => {
    const taskToMigrate = tasks.find(t => t.id === taskId);
    if (!taskToMigrate) {
      console.warn(`Task with ID ${taskId} not found for scope migration.`);
      return;
    }

    const oldScope = taskToMigrate.scope;
    if (oldScope === newScope) {
      console.warn(`Task ${taskId} is already in scope ${newScope}. No migration needed.`);
      return;
    }

    if (newScope === 'household' && (!currentHouseholdId || (userPermissions && !userPermissions.canManageTasks && !(taskToMigrate.templateType === TemplateType.GROCERY && userPermissions.canManageGroceries)))) {
      console.error(`Unauthorized: User lacks permission to migrate task ${taskToMigrate.title} to household scope.`);
      return;
    }
    if (oldScope === 'household' && (userPermissions && !userPermissions.canDeleteTasks && !(taskToMigrate.templateType === TemplateType.GROCERY && userPermissions.canDeleteGroceries))) {
      console.error(`Unauthorized: User lacks permission to migrate task ${taskToMigrate.title} from household scope.`);
      return;
    }

    const oldTaskRef = doc(getTaskCollectionRef(oldScope), taskId);
    const newTaskRef = doc(getTaskCollectionRef(newScope), taskId);

    const updatedTaskBase: KanbanTask = {
      ...taskToMigrate,
      scope: newScope,
      lastModifiedBy: currentUserId,
      localChangeTimestamp: Date.now(),
    } as KanbanTask;

    const dataToFirestore = removeUndefined(updatedTaskBase);

    if (Object.keys(dataToFirestore).length === 0) {
      console.error(`Error: Filtered task data for migration is empty for task ID ${taskId}.`);
      return;
    }

    try {
      await setDoc(newTaskRef, {
        ...dataToFirestore,
        createdAt: taskToMigrate.createdAt ? new Date(taskToMigrate.createdAt) : serverTimestamp(),
        localChangeTimestamp: serverTimestamp(),
      });

      await deleteDoc(oldTaskRef);

      createLog({
        actorId: currentUserId,
        actionType: 'SCOPE_MIGRATE',
        taskTitle: taskToMigrate.title,
        scope: newScope,
        metadata: { fromScope: oldScope, toScope: newScope },
      });

      dispatch({ type: 'REMOVE_TASK', payload: taskId });
      dispatch({ type: 'ADD_TASK', payload: { ...updatedTaskBase, createdAt: taskToMigrate.createdAt } });
    } catch (e) {
      console.error(`Error migrating task ${taskId} from ${oldScope} to ${newScope}:`, e);
    }
  }, [tasks, currentUserId, currentHouseholdId, userPermissions, getTaskCollectionRef, createLog, dispatch]);

  // --- 🛰️ REAL-TIME FIRESTORE SUBSCRIPTIONS ---
  // Independent subscriptions for personal and household tasks.
  // Household listener is NOT nested inside personal callback to avoid listener leak.
  useEffect(() => {
    if (!currentUserId) return;

    const unsubscribers: (() => void)[] = [];
    let hasInitialData = false;
    const allTasks: KanbanTask[] = [];

    const personalTasksRef = collection(db, 'users', currentUserId, 'personalTasks');
    const personalUnsub = onSnapshot(
      personalTasksRef,
      (snapshot) => {
        snapshot.docChanges().forEach((change) => {
          const task = change.doc.data() as KanbanTask;
          const index = allTasks.findIndex(t => t.id === task.id);

          // Soft-deleted tasks (modified with isDeleted=true) must be removed from allTasks
          if (task.isDeleted) {
            if (index > -1) allTasks.splice(index, 1);
            return;
          }

          if (change.type === 'added' || change.type === 'modified') {
            if (index > -1) {
              allTasks[index] = task;
            } else {
              allTasks.push(task);
            }
          } else if (change.type === 'removed') {
            if (index > -1) {
              allTasks.splice(index, 1);
            }
          }
        });

        if (!hasInitialData) {
          hasInitialData = true;
          setIsLoading(false);
        }
        setTasks([...allTasks]);
        setError(null);
      },
      (err) => {
        setError(`Personal tasks sync error: ${err.message}`);
        setIsLoading(false);
      }
    );
    unsubscribers.push(personalUnsub);

    if (currentHouseholdId && (activeDataScope === 'SHARED' || activeDataScope === 'BOTH')) {
      const householdTasksRef = collection(db, 'households', currentHouseholdId, 'tasks');
      const householdUnsub = onSnapshot(
        householdTasksRef,
        (snapshot) => {
          snapshot.docChanges().forEach((change) => {
            const task = change.doc.data() as KanbanTask;
            const index = allTasks.findIndex(t => t.id === task.id);

            // Soft-deleted tasks (modified with isDeleted=true) must be removed from allTasks
            if (task.isDeleted) {
              if (index > -1) allTasks.splice(index, 1);
              return;
            }

            if (change.type === 'added' || change.type === 'modified') {
              if (index > -1) {
                allTasks[index] = task;
              } else {
                allTasks.push(task);
              }
            } else if (change.type === 'removed') {
              if (index > -1) {
                allTasks.splice(index, 1);
              }
            }
          });

          if (!hasInitialData) {
            hasInitialData = true;
            setIsLoading(false);
          }
          setTasks([...allTasks]);
          setError(null);
        },
        (err) => {
          setError(`Household tasks sync error: ${err.message}`);
          setIsLoading(false);
        }
      );
      unsubscribers.push(householdUnsub);
    }

    return () => {
      unsubscribers.forEach(unsub => unsub());
    };
  }, [currentUserId, currentHouseholdId, activeDataScope]);

  return {
    tasks,
    logs,
    moveTask,
    createTask,
    consumeQuantity,
    deleteTask,
    updateTask,
    migrateTaskScope,
    isOnline,
    isLoading,
    error,
    syncStatus,
    pendingSyncCount: pendingSyncQueue.length,
    pendingSyncQueue,
    reconcileOfflineChanges,
    retryPendingSync,
    clearPendingSync
  };
};
